import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { cadastrosFinanceirosService } from '../services/cadastrosFinanceirosService';
import type {
  SalvarBancoRequest,
  SalvarContaCorrenteRequest,
  SalvarCartaoRequest,
  SalvarFormaPagamentoRequest,
  SalvarCondicaoPagamentoRequest,
} from '../types';

// ── Combos auxiliares ──────────────────────────────────────────────────────
export const useCentrosCusto = () => {
  const { data = [], isLoading } = useQuery({
    queryKey: ['centros-custo'],
    queryFn: cadastrosFinanceirosService.getCentrosCusto,
    staleTime: 5 * 60_000,
  });
  return { centrosCusto: data, isLoading };
};

export const useGruposDespesas = () => {
  const { data = [], isLoading } = useQuery({
    queryKey: ['grupos-despesas'],
    queryFn: cadastrosFinanceirosService.getGruposDespesas,
    staleTime: 5 * 60_000,
  });
  return { gruposDespesas: data, isLoading };
};

export const useTiposDespesas = (grupoDespesasId?: number) => {
  const { data = [], isLoading } = useQuery({
    queryKey: ['tipos-despesas', grupoDespesasId],
    queryFn: () => cadastrosFinanceirosService.getTiposDespesas(grupoDespesasId),
    staleTime: 5 * 60_000,
  });
  return { tiposDespesas: data, isLoading };
};

// ── Bancos ─────────────────────────────────────────────────────────────────
export const useBancosCombo = () => {
  const { data = [], isLoading } = useQuery({
    queryKey: ['bancos-combo'],
    queryFn: cadastrosFinanceirosService.getBancosCombo,
    staleTime: 5 * 60_000,
  });
  return { bancos: data, isLoading };
};

export const useBancos = (busca?: string, pagina = 1, tamanhoPagina = 20) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['bancos', busca, pagina, tamanhoPagina],
    queryFn: () => cadastrosFinanceirosService.listarBancos({ busca, pagina, tamanhoPagina }),
  });

  const invalidarBancos = () => {
    queryClient.invalidateQueries({ queryKey: ['bancos'] });
    queryClient.invalidateQueries({ queryKey: ['bancos-combo'] });
  };

  const criar = useMutation({
    mutationFn: (req: SalvarBancoRequest) => cadastrosFinanceirosService.criarBanco(req),
    onSuccess: () => { invalidarBancos(); toast.success('Banco criado com sucesso!'); },
    onError: (e: Error) => toast.error(`Erro ao criar banco: ${e.message}`),
  });

  const atualizar = useMutation({
    mutationFn: ({ id, req }: { id: number; req: SalvarBancoRequest }) =>
      cadastrosFinanceirosService.atualizarBanco(id, req),
    onSuccess: () => { invalidarBancos(); toast.success('Banco atualizado com sucesso!'); },
    onError: (e: Error) => toast.error(`Erro ao atualizar banco: ${e.message}`),
  });

  const excluir = useMutation({
    mutationFn: (id: number) => cadastrosFinanceirosService.excluirBanco(id),
    onSuccess: () => { invalidarBancos(); toast.success('Banco excluído com sucesso!'); },
    onError: (e: Error) => toast.error(`Erro ao excluir banco: ${e.message}`),
  });

  return {
    response: query.data,
    isLoading: query.isLoading,
    criar: criar.mutate,
    atualizar: atualizar.mutate,
    excluir: excluir.mutate,
    isCriando: criar.isPending,
    isAtualizando: atualizar.isPending,
    isExcluindo: excluir.isPending,
  };
};

// ── Contas Correntes ───────────────────────────────────────────────────────
export const useContasCorrentesCombo = () => {
  const { data = [], isLoading } = useQuery({
    queryKey: ['contas-correntes-combo'],
    queryFn: cadastrosFinanceirosService.getContasCorrentesCombo,
    staleTime: 5 * 60_000,
  });
  return { contasCorrentes: data, isLoading };
};

export const useContasCorrentes = (busca?: string, pagina = 1, tamanhoPagina = 20) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['contas-correntes', busca, pagina, tamanhoPagina],
    queryFn: () => cadastrosFinanceirosService.listarContasCorrentes({ busca, pagina, tamanhoPagina }),
  });

  const invalidarContasCorrentes = () => {
    queryClient.invalidateQueries({ queryKey: ['contas-correntes'] });
    queryClient.invalidateQueries({ queryKey: ['contas-correntes-combo'] });
  };

  const criar = useMutation({
    mutationFn: (req: SalvarContaCorrenteRequest) => cadastrosFinanceirosService.criarContaCorrente(req),
    onSuccess: () => { invalidarContasCorrentes(); toast.success('Conta corrente criada com sucesso!'); },
    onError: (e: Error) => toast.error(`Erro ao criar conta corrente: ${e.message}`),
  });

  const atualizar = useMutation({
    mutationFn: ({ id, req }: { id: number; req: SalvarContaCorrenteRequest }) =>
      cadastrosFinanceirosService.atualizarContaCorrente(id, req),
    onSuccess: () => { invalidarContasCorrentes(); toast.success('Conta corrente atualizada com sucesso!'); },
    onError: (e: Error) => toast.error(`Erro ao atualizar conta corrente: ${e.message}`),
  });

  const inativar = useMutation({
    mutationFn: (id: number) => cadastrosFinanceirosService.inativarContaCorrente(id),
    onSuccess: () => { invalidarContasCorrentes(); toast.success('Conta corrente inativada!'); },
    onError: (e: Error) => toast.error(`Erro ao inativar conta corrente: ${e.message}`),
  });

  return {
    response: query.data,
    isLoading: query.isLoading,
    criar: criar.mutate,
    atualizar: atualizar.mutate,
    inativar: inativar.mutate,
    isCriando: criar.isPending,
    isAtualizando: atualizar.isPending,
    isInativando: inativar.isPending,
  };
};

// ── Cartões ────────────────────────────────────────────────────────────────
export const useCartoesCombo = () => {
  const { data = [], isLoading } = useQuery({
    queryKey: ['cartoes-combo'],
    queryFn: cadastrosFinanceirosService.getCartoesCombo,
    staleTime: 5 * 60_000,
  });
  return { cartoes: data, isLoading };
};

export const useCartoes = (busca?: string, pagina = 1, tamanhoPagina = 20) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['cartoes', busca, pagina, tamanhoPagina],
    queryFn: () => cadastrosFinanceirosService.listarCartoes({ busca, pagina, tamanhoPagina }),
  });

  const criar = useMutation({
    mutationFn: (req: SalvarCartaoRequest) => cadastrosFinanceirosService.criarCartao(req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cartoes'] });
      toast.success('Cartão criado com sucesso!');
    },
    onError: (e: Error) => toast.error(`Erro ao criar cartão: ${e.message}`),
  });

  const atualizar = useMutation({
    mutationFn: ({ id, req }: { id: number; req: SalvarCartaoRequest }) =>
      cadastrosFinanceirosService.atualizarCartao(id, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cartoes'] });
      toast.success('Cartão atualizado com sucesso!');
    },
    onError: (e: Error) => toast.error(`Erro ao atualizar cartão: ${e.message}`),
  });

  const inativar = useMutation({
    mutationFn: (id: number) => cadastrosFinanceirosService.inativarCartao(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cartoes'] });
      toast.success('Cartão inativado!');
    },
    onError: (e: Error) => toast.error(`Erro ao inativar cartão: ${e.message}`),
  });

  return {
    response: query.data,
    isLoading: query.isLoading,
    criar: criar.mutate,
    atualizar: atualizar.mutate,
    inativar: inativar.mutate,
    isCriando: criar.isPending,
    isAtualizando: atualizar.isPending,
    isInativando: inativar.isPending,
  };
};

// ── Formas de Pagamento ────────────────────────────────────────────────────
export const useFormasPagamentoCombo = (vendaPrazo?: boolean) => {
  const { data = [], isLoading } = useQuery({
    queryKey: ['formas-pagamento-combo', vendaPrazo],
    queryFn: () => cadastrosFinanceirosService.getFormasPagamentoCombo(vendaPrazo),
    staleTime: 5 * 60_000,
  });
  return { formasPagamento: data, isLoading };
};

export const useFormasPagamento = (busca?: string, pagina = 1, tamanhoPagina = 20) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['formas-pagamento', busca, pagina, tamanhoPagina],
    queryFn: () => cadastrosFinanceirosService.listarFormasPagamento({ busca, pagina, tamanhoPagina }),
  });

  const invalidarFormasPagamento = () => {
    queryClient.invalidateQueries({ queryKey: ['formas-pagamento'] });
    queryClient.invalidateQueries({ queryKey: ['formas-pagamento-combo'] });
  };

  const criar = useMutation({
    mutationFn: (req: SalvarFormaPagamentoRequest) => cadastrosFinanceirosService.criarFormaPagamento(req),
    onSuccess: () => { invalidarFormasPagamento(); toast.success('Forma de pagamento criada com sucesso!'); },
    onError: (e: Error) => toast.error(`Erro ao criar forma de pagamento: ${e.message}`),
  });

  const atualizar = useMutation({
    mutationFn: ({ id, req }: { id: number; req: SalvarFormaPagamentoRequest }) =>
      cadastrosFinanceirosService.atualizarFormaPagamento(id, req),
    onSuccess: () => { invalidarFormasPagamento(); toast.success('Forma de pagamento atualizada com sucesso!'); },
    onError: (e: Error) => toast.error(`Erro ao atualizar forma de pagamento: ${e.message}`),
  });

  const inativar = useMutation({
    mutationFn: (id: number) => cadastrosFinanceirosService.inativarFormaPagamento(id),
    onSuccess: () => { invalidarFormasPagamento(); toast.success('Forma de pagamento inativada!'); },
    onError: (e: Error) => toast.error(`Erro ao inativar forma de pagamento: ${e.message}`),
  });

  return {
    response: query.data,
    isLoading: query.isLoading,
    criar: criar.mutate,
    atualizar: atualizar.mutate,
    inativar: inativar.mutate,
    isCriando: criar.isPending,
    isAtualizando: atualizar.isPending,
    isInativando: inativar.isPending,
  };
};

// ── Condições de Pagamento ─────────────────────────────────────────────────
export const useCondicoesPagamentoCombo = (formaPagRecId?: number) => {
  const { data = [], isLoading } = useQuery({
    queryKey: ['condicoes-pagamento-combo', formaPagRecId],
    queryFn: () => cadastrosFinanceirosService.getCondicoesPagamentoCombo(formaPagRecId),
    staleTime: 5 * 60_000,
  });
  return { condicoes: data, isLoading };
};

export const useCondicoesPagamento = (formaPagRecId?: number, busca?: string, pagina = 1, tamanhoPagina = 20) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['condicoes-pagamento', formaPagRecId, busca, pagina, tamanhoPagina],
    queryFn: () => cadastrosFinanceirosService.listarCondicoesPagamento({ formaPagRecId, busca, pagina, tamanhoPagina }),
  });

  const criar = useMutation({
    mutationFn: (req: SalvarCondicaoPagamentoRequest) => cadastrosFinanceirosService.criarCondicaoPagamento(req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['condicoes-pagamento'] });
      toast.success('Condição de pagamento criada com sucesso!');
    },
    onError: (e: Error) => toast.error(`Erro ao criar condição: ${e.message}`),
  });

  const atualizar = useMutation({
    mutationFn: ({ id, req }: { id: number; req: SalvarCondicaoPagamentoRequest }) =>
      cadastrosFinanceirosService.atualizarCondicaoPagamento(id, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['condicoes-pagamento'] });
      toast.success('Condição de pagamento atualizada com sucesso!');
    },
    onError: (e: Error) => toast.error(`Erro ao atualizar condição: ${e.message}`),
  });

  const bloquear = useMutation({
    mutationFn: (id: number) => cadastrosFinanceirosService.bloquearCondicaoPagamento(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['condicoes-pagamento'] });
      toast.success('Condição de pagamento bloqueada!');
    },
    onError: (e: Error) => toast.error(`Erro ao bloquear condição: ${e.message}`),
  });

  return {
    response: query.data,
    isLoading: query.isLoading,
    criar: criar.mutate,
    atualizar: atualizar.mutate,
    bloquear: bloquear.mutate,
    isCriando: criar.isPending,
    isAtualizando: atualizar.isPending,
    isBloqueando: bloquear.isPending,
  };
};
